import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { StatusChip, type StatusTone } from '@/components/ui/Chip';
import { Select } from '@/components/ui/Select';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { Textarea } from '@/components/ui/Textarea';
import { ResponsiveTable } from '@/components/ui/Table';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import { TYPE } from '@/lib/typography';
import { uiDateLocale } from '@/lib/i18n';
import { usePatient } from '@/hooks/usePatients';
import {
  useUpdatePatientConsent,
  type ConsentStatus,
  type ConsentType,
  type ConsentRecord,
} from '@/hooks/usePatientConsents';
import type { Json } from '@/types/database.generated';

interface ConsentTabProps {
  clinicId: string;
  patientId: string;
}

function parseConsent(value: Json | null): ConsentRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const v = value as Record<string, Json>;
  return {
    status: (v.status as ConsentStatus) ?? 'not_set',
    type: v.type as ConsentType | undefined,
    method: v.method as 'email' | 'verbal' | 'written' | 'portal' | undefined,
    notes: typeof v.notes === 'string' ? v.notes : undefined,
    signed_at: typeof v.signed_at === 'string' ? v.signed_at : undefined,
    updated_at: typeof v.updated_at === 'string' ? v.updated_at : new Date().toISOString(),
  };
}

function consentStatusTone(status: ConsentStatus): StatusTone {
  switch (status) {
    case 'accepted':
      return 'completed';
    case 'pending':
      return 'draft';
    case 'rejected':
    case 'revoked':
      return 'neutral';
    case 'not_set':
      return 'neutral';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function ConsentTab({ patientId }: ConsentTabProps) {
  const { t } = useTranslation();
  const { data: patient, isLoading, error, refetch } = usePatient(patientId);
  const updateConsent = useUpdatePatientConsent();

  const [editing, setEditing] = useState(false);
  const [formStatus, setFormStatus] = useState<ConsentStatus>('accepted');
  const [formType, setFormType] = useState<ConsentType>('gdpr');
  const [formMethod, setFormMethod] = useState<'email' | 'verbal' | 'written' | 'portal'>('written');
  const [formNotes, setFormNotes] = useState('');

  const currentConsent = parseConsent(patient?.consent ?? null);
  const hasConsent = Boolean(currentConsent && currentConsent.status !== 'not_set');

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString(uiDateLocale(), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openRecord = () => {
    if (currentConsent) {
      setFormStatus(currentConsent.status === 'not_set' ? 'accepted' : currentConsent.status);
      setFormType(currentConsent.type ?? 'gdpr');
      setFormMethod(currentConsent.method ?? 'written');
      setFormNotes(currentConsent.notes ?? '');
    } else {
      setFormStatus('accepted');
      setFormType('gdpr');
      setFormMethod('written');
      setFormNotes('');
    }
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      await updateConsent.mutateAsync({
        patient_id: patientId,
        consent: {
          status: formStatus,
          type: formType,
          method: formMethod,
          notes: formNotes.trim() || undefined,
          signed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      });
      toast.success(t('patients.consent.saved'));
      setEditing(false);
      setFormNotes('');
    } catch (err) {
      console.error('Failed to update consent:', err);
      toast.error(t('patients.consent.error_saving'));
    }
  };

  return (
    <>
    <PatientTabFrame
      title={t('patients.tabs.consent')}
      count={hasConsent ? 1 : 0}
      action={
        <Button type="button" onClick={openRecord}>
          {t('patients.consent.record_consent')}
        </Button>
      }
      loading={isLoading}
      error={error ? t('patients.consent.error_loading', 'Could not load consent') : undefined}
      onRetry={() => void refetch()}
      empty={
        !isLoading && !error && !hasConsent
          ? {
              icon: <FileCheck className="h-7 w-7" />,
              title: t('patients.consent.empty_title'),
              description: t('patients.consent.empty_description'),
            }
          : undefined
      }
    >
        <div className="mt-s4">
          <ResponsiveTable
            columns={[
              {
                id: 'type',
                header: t('patients.consent.type_label'),
                cell: (row) => t(`patients.consent.type.${row.type ?? 'other'}`, row.type ?? '—'),
              },
              {
                id: 'status',
                header: t('patients.consent.status_label'),
                cell: (row) => (
                  <StatusChip tone={consentStatusTone(row.status)}>
                    {t(`patients.consent.status.${row.status}`)}
                  </StatusChip>
                ),
              },
              {
                id: 'method',
                header: t('patients.consent.method_label'),
                cell: (row) =>
                  row.method ? t(`patients.consent.method.${row.method}`) : '—',
              },
              {
                id: 'recorded',
                header: t('patients.consent.signed_at'),
                cell: (row) => formatDate(row.signed_at ?? row.updated_at),
              },
              {
                id: 'notes',
                header: t('patients.consent.notes_label'),
                cell: (row) => row.notes || '—',
              },
            ]}
            rows={[currentConsent!]}
            getRowKey={() => 'current'}
            mobileCard={(row) => (
              <div className="space-y-s2">
                <div className="flex items-center justify-between gap-s2">
                  <p className={TYPE.section}>
                    {t(`patients.consent.type.${row.type ?? 'other'}`, row.type ?? '—')}
                  </p>
                  <StatusChip tone={consentStatusTone(row.status)}>
                    {t(`patients.consent.status.${row.status}`)}
                  </StatusChip>
                </div>
                <p className={`${TYPE.small} text-ds-muted`}>
                  {row.method ? t(`patients.consent.method.${row.method}`) : '—'}
                  {' · '}
                  {formatDate(row.signed_at ?? row.updated_at)}
                </p>
                {row.notes ? <p className={`${TYPE.body} text-ds-text`}>{row.notes}</p> : null}
              </div>
            )}
          />
        </div>
    </PatientTabFrame>

      <SlideOver
        open={editing}
        title={t('patients.consent.record_consent')}
        description={t('patients.consent.info')}
        onClose={() => setEditing(false)}
        dirty={formNotes.length > 0}
        footer={
          <SlideOverFooter
            onCancel={() => setEditing(false)}
            primary={
              <Button onClick={() => void handleSave()} loading={updateConsent.isPending}>
                {t('common.save')}
              </Button>
            }
          />
        }
      >
        <div className="space-y-s4">
          <Select
            id="consent-status"
            label={t('patients.consent.status_label')}
            value={formStatus}
            onChange={(e) => setFormStatus(e.target.value as ConsentStatus)}
          >
            <option value="accepted">{t('patients.consent.status.accepted')}</option>
            <option value="rejected">{t('patients.consent.status.rejected')}</option>
            <option value="revoked">{t('patients.consent.status.revoked')}</option>
            <option value="pending">{t('patients.consent.status.pending')}</option>
          </Select>
          <Select
            id="consent-type"
            label={t('patients.consent.type_label')}
            value={formType}
            onChange={(e) => setFormType(e.target.value as ConsentType)}
          >
            <option value="gdpr">{t('patients.consent.type.gdpr')}</option>
            <option value="treatment">{t('patients.consent.type.treatment')}</option>
            <option value="marketing">{t('patients.consent.type.marketing')}</option>
            <option value="data_processing">{t('patients.consent.type.data_processing')}</option>
            <option value="other">{t('patients.consent.type.other')}</option>
          </Select>
          <Select
            id="consent-method"
            label={t('patients.consent.method_label')}
            value={formMethod}
            onChange={(e) =>
              setFormMethod(e.target.value as 'email' | 'verbal' | 'written' | 'portal')
            }
          >
            <option value="written">{t('patients.consent.method.written')}</option>
            <option value="verbal">{t('patients.consent.method.verbal')}</option>
            <option value="email">{t('patients.consent.method.email')}</option>
            <option value="portal">{t('patients.consent.method.portal')}</option>
          </Select>
          <Textarea
            id="consent-notes"
            label={`${t('patients.consent.notes_label')} (${t('common.optional')})`}
            rows={3}
            value={formNotes}
            onChange={(e) => setFormNotes(e.target.value)}
            placeholder={t('patients.consent.notes_placeholder')}
          />
        </div>
      </SlideOver>
    </>
  );
}
